import { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, Eye } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultVideos = [
  {
    id: '1',
    title: 'Bhagavad Gita Teachings',
    video_url: 'https://www.youtube.com/watch?v=1yI4cM67eTI',
    thumbnail_url: 'https://images.unsplash.com/photo-1759592370294-30f83cf6a609?w=600&h=400&fit=crop',
    creator_name: 'Spiritual Wisdom',
    views: 12500,
  },
  {
    id: '2',
    title: 'Morning Meditation Guide',
    video_url: 'https://www.youtube.com/watch?v=3y4CMLDw8H8',
    thumbnail_url: 'https://images.unsplash.com/photo-1723707358389-7b47690dff16?w=600&h=400&fit=crop',
    creator_name: 'Peace & Calm',
    views: 8900,
  },
  {
    id: '3',
    title: 'Stories from Ramayana',
    video_url: 'https://www.youtube.com/watch?v=c9P6XcWBUVo',
    thumbnail_url: 'https://images.unsplash.com/photo-1767533427730-cc8791c14558?w=600&h=400&fit=crop',
    creator_name: 'Epic Tales',
    views: 15600,
  },
  {
    id: '4',
    title: 'Yoga for Beginners',
    video_url: 'https://www.youtube.com/watch?v=1yI4cM67eTI',
    thumbnail_url: 'https://images.unsplash.com/photo-1769888913192-897f2f097d04?w=600&h=400&fit=crop',
    creator_name: 'Wellness Journey',
    views: 22000,
  },
];

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      if (response.data.length > 0) {
        setVideos(response.data);
      } else {
        setVideos(defaultVideos);
      }
    } catch (error) {
      setVideos(defaultVideos);
    } finally {
      setLoading(false);
    }
  };

  const formatViews = (views) => {
    if (views >= 1000) {
      return `${(views / 1000).toFixed(1)}K`;
    }
    return views.toString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="text-4xl text-[#FF9933] mb-2">ॐ</div>
          <p className="text-slate-600">Loading videos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <div className="text-center mb-8 peaceful-enter">
        <h2 className="text-3xl font-bold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          Bhakti Videos
        </h2>
        <p className="text-slate-600">Devotional content and spiritual wisdom</p>
      </div>

      {playingVideo && (
        <div className="mb-8 bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden peaceful-enter">
          <div className="aspect-video">
            <iframe
              src={playingVideo.replace('watch?v=', 'embed/')}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
          <div className="p-4">
            <button
              data-testid="close-video-btn"
              onClick={() => setPlayingVideo(null)}
              className="text-sm text-slate-600 hover:text-[#FF9933] transition-colors"
            >
              Close Video
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {videos.map((video, index) => (
          <div
            key={video.id}
            data-testid={`video-card-${index}`}
            className="video-card bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden cursor-pointer peaceful-enter"
            onClick={() => setPlayingVideo(video.video_url)}
            style={{ animationDelay: `${index * 0.05}s` }}
          >
            <div className="relative group">
              <img
                src={video.thumbnail_url}
                alt={video.title}
                className="w-full aspect-video object-cover"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <div className="w-12 h-12 bg-white/90 group-hover:bg-white rounded-full flex items-center justify-center transition-all transform group-hover:scale-110">
                  <Play className="w-6 h-6 text-[#FF9933] ml-0.5" fill="#FF9933" />
                </div>
              </div>
            </div>

            <div className="p-4">
              <h3 className="font-semibold text-slate-800 text-sm mb-1 line-clamp-2">
                {video.title}
              </h3>
              <p className="text-xs text-slate-600 mb-2">{video.creator_name}</p>
              <div className="flex items-center gap-1 text-xs text-slate-500">
                <Eye className="w-3 h-3" />
                <span>{formatViews(video.views)} views</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-12 text-center bg-orange-50/50 rounded-2xl p-8 border border-orange-100 peaceful-enter">
        <div className="text-3xl text-[#FF9933] mb-3">ॐ</div>
        <h3 className="text-xl font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
          No Endless Scrolling
        </h3>
        <p className="text-slate-600">Click to watch. Pause when you need. Stay mindful.</p>
      </div>
    </div>
  );
}
